import FieldValue from '@/components/console/field-value';
import EmptyState from '@/components/empty-state';
import PageHeader from '@/components/page-header';
import Pagination from '@/components/pagination';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import InvoiceStatusPill from '@/pages/apps/components/invoice-status-pill';
import {
  app_list_records_path,
  edit_app_list_record_path,
  new_app_list_record_path,
} from '@/rails/routes';
import type { PaginationData } from '@/types/pagination';
import type { AppList, AppRecord, AppShell, Customer } from '@/types/platform';
import { Link, router } from '@inertiajs/react';

interface RecordsIndexProps {
  app: AppShell;
  list: AppList;
  records: AppRecord[];
  customers: Customer[];
  pagination: PaginationData;
}

const MAX_EXTRA_COLUMNS = 5;

const RecordsIndex = ({ app, list, records, customers, pagination }: RecordsIndexProps) => {
  const plural = list.name.toLowerCase();
  const newPath = new_app_list_record_path(app.id, list.key);
  const titleField = list.fields.find((field) => field.key === list.titleField);
  const columns = list.fields
    .filter(
      (field) =>
        field.key !== list.titleField && field.type !== 'attachments' && field.type !== 'long_text'
    )
    .slice(0, MAX_EXTRA_COLUMNS);
  // These two columns only earn their space when the list, or a record on this page, uses them.
  const showAttachments =
    list.fields.some((field) => field.type === 'attachments') ||
    records.some((record) => record.attachments.length > 0);
  const showInvoices = list.billable || records.some((record) => record.invoices.length > 0);
  const newButton = (
    <Button asChild>
      <Link href={newPath}>New {list.singular.toLowerCase()}</Link>
    </Button>
  );

  return (
    <div className="space-y-6">
      <PageHeader
        title={list.name}
        description={`${pagination.total} ${pagination.total === 1 ? list.singular.toLowerCase() : plural}`}
        actions={newButton}
      />

      <Card className="min-w-0">
        <CardContent>
          {records.length === 0 ? (
            <EmptyState
              title={`No ${plural} yet`}
              description={`Add the first ${list.singular.toLowerCase()} and it shows up here.`}
              action={newButton}
            />
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{titleField?.label ?? list.singular}</TableHead>
                  {columns.map((field) => (
                    <TableHead key={field.key}>{field.label}</TableHead>
                  ))}
                  {showAttachments ? <TableHead>Attachments</TableHead> : null}
                  {showInvoices ? <TableHead>Invoice</TableHead> : null}
                </TableRow>
              </TableHeader>
              <TableBody>
                {records.map((record) =>
                  record.id ? (
                    <RecordRow
                      key={record.id}
                      href={edit_app_list_record_path(app.id, list.key, record.id)}
                      record={record}
                      columns={columns}
                      customers={customers}
                      showAttachments={showAttachments}
                      showInvoices={showInvoices}
                    />
                  ) : null
                )}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Pagination
        pagination={pagination}
        pathBuilder={(params) =>
          app_list_records_path(app.id, list.key, params as { page?: number })
        }
      />
    </div>
  );
};

interface RecordRowProps {
  href: string;
  record: AppRecord;
  columns: AppList['fields'];
  customers: Customer[];
  showAttachments: boolean;
  showInvoices: boolean;
}

const RecordRow = ({
  href,
  record,
  columns,
  customers,
  showAttachments,
  showInvoices,
}: RecordRowProps) => {
  // The newest invoice says where the record stands. Any earlier ones are counted.
  const latestInvoice = record.invoices[record.invoices.length - 1];

  return (
    <TableRow className="cursor-pointer" onClick={() => router.visit(href)}>
      <TableCell className="max-w-64 truncate font-medium">
        {/* The whole row opens the record; this link is the keyboard and screen reader way in. */}
        <Link href={href} className="underline" onClick={(event) => event.stopPropagation()}>
          {record.title}
        </Link>
      </TableCell>
      {columns.map((field) => (
        <TableCell key={field.key} className="max-w-56 truncate">
          <FieldValue
            field={field}
            value={record.values.find((pair) => pair.key === field.key)?.value}
            customers={customers}
          />
        </TableCell>
      ))}
      {showAttachments ? (
        <TableCell className="tabular-nums">{record.attachments.length}</TableCell>
      ) : null}
      {showInvoices ? (
        <TableCell>
          {latestInvoice ? (
            <span className="inline-flex items-center gap-1.5">
              <InvoiceStatusPill status={latestInvoice.status} />
              {record.invoices.length > 1 ? (
                <span className="text-muted-foreground text-xs">
                  +{record.invoices.length - 1} more
                </span>
              ) : null}
            </span>
          ) : (
            <span className="text-muted-foreground">Not invoiced</span>
          )}
        </TableCell>
      ) : null}
    </TableRow>
  );
};

export default RecordsIndex;
